import { useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { challengeMethods, resendTwoStepCode, type TwoStepChallenge } from '@/api/auth';

interface Props {
  challenge: TwoStepChallenge;
  /** Back to email + password (also where an expired sign-in goes). */
  onBack: () => void;
}

function message(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

type Method = TwoStepChallenge['channel'];

function whereTheCodeIs(c: TwoStepChallenge, method: Method): string {
  if (method === 'totp') return 'Open your authenticator app and enter the 6-digit code for Bread List.';
  if (method === 'sms') return `We texted a 6-digit code to ${c.phone_hint ?? 'your phone'}.`;
  return `We emailed a 6-digit code to ${c.email}.`;
}

const SWITCH_LABEL: Record<Method, string> = {
  totp: 'Use my authenticator app',
  sms: 'Text me a code instead',
  email: 'Email me a code instead',
};

/**
 * The second step of signing in (two-step sign-in is on for this account). The password step
 * already passed; the gateway holds the session until this code (or a backup code) is right.
 */
export default function TwoStepCodeForm({ challenge, onBack }: Props) {
  const { finishTwoStep } = useAuth();
  const [code, setCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [error, setError] = useState(challenge.error ?? '');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  // The account's default comes first; any other method that is on can be picked instead.
  const [method, setMethod] = useState<Method>(challenge.channel);
  const others = challengeMethods(challenge).filter((m) => m !== method);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) {
      setError(useBackup ? 'Enter one of your backup codes.' : 'Enter the 6-digit code.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      await finishTwoStep(challenge.challenge, code.trim());
    } catch (err) {
      setError(message(err, 'That code didn’t work. Try again.'));
      setBusy(false);
    }
  };

  /** Send a code by text or email: again on the same method, or after switching to it. */
  const send = async (to: 'sms' | 'email') => {
    setError('');
    setNotice('');
    setResending(true);
    try {
      await resendTwoStepCode(challenge.challenge, to);
      setNotice(to === 'sms' ? 'We sent a new code by text.' : 'We sent a new code by email.');
    } catch (err) {
      setError(message(err, 'Could not send a new code. Try again in a minute.'));
    } finally {
      setResending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" noValidate data-testid="two-step-form">
      <p className="text-sm text-brand-ink">
        {useBackup ? 'Enter one of the backup codes you saved when you turned on two-step sign-in. Each one works once.' : whereTheCodeIs(challenge, method)}
      </p>

      {error && (
        <div role="alert" className="flex items-start gap-2.5 rounded-lg border border-brand-error/30 bg-brand-error-soft px-3.5 py-2.5 text-sm text-brand-error">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {notice && !error && (
        <div role="status" className="rounded-lg border border-brand-border bg-brand-lilac-soft px-3.5 py-2.5 text-sm text-brand-ink">
          {notice}
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="two-step-code">{useBackup ? 'Backup code' : 'Code'}</Label>
        <Input
          id="two-step-code"
          autoFocus
          autoComplete="one-time-code"
          inputMode={useBackup ? 'text' : 'numeric'}
          placeholder={useBackup ? 'abcd-efgh' : '123456'}
          maxLength={useBackup ? 20 : 6}
          value={code}
          onChange={(e) => setCode(useBackup ? e.target.value : e.target.value.replace(/\D/g, ''))}
          className="tracking-widest"
        />
      </div>

      <Button type="submit" className="h-11 w-full text-sm" disabled={busy}>
        {busy ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Checking...
          </>
        ) : (
          'Verify and sign in'
        )}
      </Button>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <button
          type="button"
          className="font-medium text-brand-plum hover:underline"
          onClick={() => {
            setUseBackup(!useBackup);
            setCode('');
            setError('');
          }}
        >
          {useBackup ? 'Use my code instead' : 'Use a backup code'}
        </button>
        {!useBackup && method !== 'totp' && (
          <button
            type="button"
            className="font-medium text-brand-plum hover:underline disabled:opacity-40"
            disabled={resending}
            onClick={() => void send(method)}
          >
            {resending ? 'Sending...' : 'Send a new code'}
          </button>
        )}
        <button type="button" className="font-medium text-brand-ink-soft hover:underline" onClick={onBack}>
          Back
        </button>
      </div>

      {!useBackup && others.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-brand-border pt-3 text-xs" data-testid="two-step-other-ways">
          {others.map((m) => (
            <button
              key={m}
              type="button"
              className="font-medium text-brand-plum hover:underline disabled:opacity-40"
              disabled={resending}
              onClick={() => {
                setMethod(m);
                setCode('');
                if (m === 'totp') {
                  setError('');
                  setNotice('');
                } else {
                  void send(m);
                }
              }}
            >
              {SWITCH_LABEL[m]}
            </button>
          ))}
        </div>
      )}
    </form>
  );
}
